import { useParams, useNavigate, useLocation } from "react-router-dom";
import { idCompress, idDecompress } from "../utils/base62";

export type UrlParams = {
  product?: string;
  category?: string;
  subcategory?: string;
  dynamicMaterialId?: string;
  subproduct?: string;
};

const setHash = (newVal: string) => {
  if (typeof window.history.pushState === "function") {
    window.history.pushState(null, "", "#" + newVal);
  } else {
    window.location.hash = newVal;
  }
};

const buildUrl = ({
  product,
  category,
  subcategory,
  dynamicMaterialId,
  subproduct,
}: UrlParams) => {
  if (subproduct) {
    return `/productBuilder/${product}/subproduct/${subproduct}`;
  }
  if (!product) {
    return "/productBuilder";
  }
  if (dynamicMaterialId) {
    return `/productBuilder/${product}/M/${dynamicMaterialId}`;
  }
  if (!category) {
    return `/productBuilder/${product}`;
  }
  if (!subcategory) {
    return `/productBuilder/${product}/${category}`;
  }
  return `/productBuilder/${product}/${category}/${subcategory}`;
};

export const useProductVariables = () => {
  const params = useParams<UrlParams>();
  const { hash } = useLocation();
  const nav = useNavigate();

  const navigateWithNewTab = (
    url: string,
    openInNewTab?: boolean,
    state?: any
  ) => {
    if (openInNewTab) {
      if (!url.startsWith("/")) url = "/" + url;
      window.open(url, "_blank");
    } else {
      nav(url, {
        state,
      });
    }
  };
  const setProduct = (productId?: string, openInNewTab?: boolean) => {
    const url = buildUrl({
      ...params,
      product: idCompress(productId),
      category: undefined,
      dynamicMaterialId: undefined,
      subproduct: undefined,
    });
    navigateWithNewTab(url, openInNewTab);
  };
  const setSubProduct = (
    subProductId?: string,
    productId?: string,
    openInNewTab?: boolean
  ) => {
    const url = buildUrl({
      ...params,
      product: productId,
      category: undefined,
      dynamicMaterialId: undefined,
      subproduct: subProductId,
    });
    navigateWithNewTab(url, openInNewTab);
  };
  const setCategory = (categoryId?: string, openInNewTab?: boolean) => {
    navigateWithNewTab(
      buildUrl({
        ...params,
        category: idCompress(categoryId),
        subcategory: undefined,
      }),
      openInNewTab
    );
  };
  const setSubCategory = (
    subcategoryId?: string | undefined,
    openInNewTab?: boolean,
    state?: any
  ) => {
    navigateWithNewTab(
      buildUrl({ ...params, subcategory: idCompress(subcategoryId) }),
      openInNewTab,
      state
    );
  };
  const setTab = (tabName: string | undefined) => {
    setHash(tabName || "");
  };

  const setDynamicMaterial = (
    dynamicMaterialId?: string,
    openInNewTab?: boolean
  ) => {
    navigateWithNewTab(
      buildUrl({
        ...params,
        dynamicMaterialId: idCompress(dynamicMaterialId),
      }),
      openInNewTab
    );
  };
  const tab = hash.replace(/^#/, "");

  const { product, category, subcategory, subproduct } = params;
  return {
    ...params,
    tab,
    product: idDecompress(product),
    category: idDecompress(category),
    subcategory: idDecompress(subcategory),
    dynamicMaterialId: idDecompress(params.dynamicMaterialId),
    subproduct: idDecompress(subproduct),
    setProduct,
    setCategory,
    setSubCategory,
    setDynamicMaterial,
    setTab,
    setSubProduct,
  };
};
